// src/pages/ModulesList.js
import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-hot-toast';
import Layout from '../Layout/Layout';
import PaymentModal from '../components/PaymentModal';
import { FiLock, FiCheck, FiPlay, FiFileText, FiClock, FiShield } from 'react-icons/fi';
import { motion } from 'framer-motion';
import api from '../utils/apiClient';
const ModulesHeader = styled.div`
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing[6]};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[6]};
  border: 1px solid ${({ theme }) => theme.colors.divider};
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${({ theme }) => theme.spacing[4]};
  }
`;

const CourseImage = styled.div`
  width: 120px;
  height: 120px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ gradient }) => gradient || 'linear-gradient(135deg, #0AFF70 0%, #09B6FF 100%)'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    width: 80px;
    height: 80px;
  }
`;

const CourseImageLetter = styled.div`
  font-size: 56px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.8);
  
  @media (max-width: 768px) {
    font-size: 40px;
  }
`;

const CourseInfo = styled.div`
  flex: 1;
`;

const CourseTitle = styled.h1`
  font-size: ${({ theme }) => theme.fonts.sizes['2xl']};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

const CourseDescription = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing[3]};
`;

const CourseStats = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[4]};
  flex-wrap: wrap;
`;

const Stat = styled.div`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fonts.sizes.sm};
  
  svg {
    margin-right: ${({ theme }) => theme.spacing[1]};
  }
`;

const ProgressContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
  justify-content: flex-end;
  align-items: flex-end;
  
  @media (max-width: 768px) {
    align-items: flex-start;
    width: 100%;
  }
`;

const ProgressText = styled.div`
  display: flex;
  justify-content: space-between;
  width: 200px;
  font-size: ${({ theme }) => theme.fonts.sizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  
  span.value {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 600;
  }
`;

const ProgressBar = styled.div`
  width: 200px;
  height: 8px;
  background-color: ${({ theme }) => theme.colors.backgroundLighter};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  overflow: hidden;
`;

const ProgressFill = styled.div`
  width: ${({ progress }) => `${progress}%`};
  height: 100%;
  background: ${({ theme }) => theme.colors.gradientPrimary};
  transition: width 0.5s ease;
`;

const ModulesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const Module = styled(motion.div)`
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.divider};
`;

const ModuleHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing[4]} ${({ theme }) => theme.spacing[6]};
  background-color: ${({ theme }) => theme.colors.backgroundLighter};
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
  cursor: pointer;
`;

const ModuleNumber = styled.div`
  width: 30px;
  height: 30px;
  background: ${({ completed, theme }) =>
        completed ? theme.colors.success : theme.colors.divider};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: ${({ theme }) => theme.fonts.sizes.sm};
  margin-right: ${({ theme }) => theme.spacing[3]};
  color: ${({ completed, theme }) =>
        completed ? theme.colors.background : theme.colors.textSecondary};
`;

const ModuleTitle = styled.div`
  font-weight: 600;
  font-size: ${({ theme }) => theme.fonts.sizes.lg};
  flex: 1;
  display: flex;
  align-items: center;
`;

const ModuleInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const ModuleCompletion = styled.div`
  display: flex;
  align-items: center;
  color: ${({ completed, theme }) =>
        completed ? theme.colors.success : theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fonts.sizes.sm};
  
  svg {
    margin-right: ${({ theme }) => theme.spacing[1]};
  }
`;

const ModuleContent = styled.div`
  padding: ${({ theme }) => theme.spacing[4]} ${({ theme }) => theme.spacing[6]};
  display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
`;

const LessonsList = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const Lesson = styled(Link)`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing[3]};
  background-color: ${({ theme }) => theme.colors.backgroundLighter};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.textPrimary};
  border: 1px solid ${({ theme }) => theme.colors.divider};
  position: relative;
  overflow: hidden;
  
  ${({ completed, theme }) => completed && `
    border-left: 3px solid ${theme.colors.success};
  `}
  
  ${({ current, theme }) => current && `
    border-left: 3px solid ${theme.colors.primary};
    background-color: ${theme.colors.cardGlow};
  `}
  
  ${({ locked, theme }) => locked && `
    opacity: 0.6;
    cursor: not-allowed;
  `}
  
  &:hover {
    background-color: ${({ locked, theme }) => !locked && theme.colors.backgroundLighter};
    transform: ${({ locked }) => !locked && 'translateY(-2px)'};
  }
`;

const LessonType = styled.div`
  width: 36px;
  height: 36px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background-color: ${({ locked, theme }) =>
        locked ? theme.colors.backgroundLight : theme.colors.glow};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: ${({ theme }) => theme.spacing[3]};
  color: ${({ locked, theme }) =>
        locked ? theme.colors.textSecondary : theme.colors.primary};
`;

const LessonContent = styled.div`
  flex: 1;
`;

const LessonTitle = styled.div`
  font-weight: 500;
  margin-bottom: 2px;
`;

const LessonDuration = styled.div`
  font-size: ${({ theme }) => theme.fonts.sizes.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 4px;
  }
`;

const LessonStatus = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background-color: ${({ completed, theme }) =>
        completed ? theme.colors.success : 'transparent'};
  color: ${({ completed, theme }) =>
        completed ? theme.colors.background : theme.colors.textSecondary};
  margin-left: ${({ theme }) => theme.spacing[2]};
`;

const QuizLink = styled(Link)`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing[3]};
  background-color: ${({ theme }) => theme.colors.backgroundLighter};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.textPrimary};
  border: 1px solid ${({ theme }) => theme.colors.primary}40;
  position: relative;
  margin-top: ${({ theme }) => theme.spacing[3]};
  
  ${({ completed, theme }) => completed && `
    border-left: 3px solid ${theme.colors.success};
  `}
  
  ${({ locked, theme }) => locked && `
    opacity: 0.6;
    cursor: not-allowed;
    border-color: ${theme.colors.divider};
  `}
  
  &:hover {
    background-color: ${({ locked, theme }) => !locked && theme.colors.glow}10;
    transform: ${({ locked }) => !locked && 'translateY(-2px)'};
  }
`;

const QuizIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background-color: ${({ locked, theme }) =>
        locked ? theme.colors.backgroundLight : theme.colors.primary}20;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: ${({ theme }) => theme.spacing[3]};
  color: ${({ locked, theme }) =>
        locked ? theme.colors.textSecondary : theme.colors.primary};
`;

const FinalQuizButton = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing[4]};
  margin-top: ${({ theme }) => theme.spacing[6]};
  background: ${({ theme }) => theme.colors.gradientPrimary};
  color: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-weight: 600;
  font-size: ${({ theme }) => theme.fonts.sizes.lg};
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.glow};
  }
  
  ${({ locked, theme }) => locked && `
    opacity: 0.6;
    cursor: not-allowed;
    background: ${theme.colors.backgroundLighter};
    color: ${theme.colors.textSecondary};
    
    &:hover {
      transform: none;
      box-shadow: none;
    }
  `}
`;

const LockedIcon = styled(FiLock)`
  margin-left: ${({ theme }) => theme.spacing[2]};
`;

const PaymentBlockedContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: ${({ theme }) => theme.spacing[6]};
  text-align: center;
`;

const PaymentBlockedIcon = styled(FiShield)`
  font-size: 4rem;
  color: ${({ theme }) => theme.colors.warning};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const PaymentBlockedTitle = styled.h2`
  font-size: ${({ theme }) => theme.fonts.sizes['2xl']};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

const PaymentBlockedMessage = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fonts.sizes.lg};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
  max-width: 600px;
`;

const PaymentButton = styled.button`
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[6]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.fonts.sizes.base};
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
    transform: translateY(-1px);
    box-shadow: ${({ theme }) => theme.shadows.medium};
  }
`;

const BackButton = styled(Link)`
  margin-top: ${({ theme }) => theme.spacing[4]};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-decoration: none;
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;


const ModulesList = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openModules, setOpenModules] = useState({});
  const [progress, setProgress] = useState(0);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [accessBlocked, setAccessBlocked] = useState(false);

  useEffect(() => {
    // Fetch course and modules data
    const fetchData = async () => {
      try {
        setLoading(true);

        // First get course details to check payment status
        const courseDetails = await api.courses.getById(courseId);

        // **PAYMENT PROTECTION**: Check if user has access to course content
        if (!courseDetails.is_free && !courseDetails.has_purchased && !courseDetails.enrolled) {
          setAccessBlocked(true);
          setCourse(courseDetails);
          setLoading(false);
          return;
        }

        // Use the API client to fetch course structure
        const data = await api.courses.getStructure(courseId);
        // API response received successfully

        // Merge courseDetails with structure data to preserve stats from getById
        if (data && data.course) {
          setCourse({
            ...courseDetails,
            ...data.course
          });
        } else {
          // If no course in structure response, use courseDetails
          setCourse(courseDetails);
        }

        if (data && Array.isArray(data.modules)) {
          setModules(data.modules);
        } else {
          setModules([]);
        }

        // Get progress from the API response
        setProgress(data && typeof data.progress === 'number' ? data.progress : 0);

        // Set first module as open by default if modules exist
        if (data && data.modules && data.modules.length > 0) {
          setOpenModules({ [data.modules[0].id]: true });
        }

        setLoading(false);
      } catch (error) {
        console.error('Error fetching course structure:', error);

        // Check if error is due to payment required
        if (error.response?.status === 402 || error.response?.data?.requires_payment) {
          setAccessBlocked(true);
          toast.error('Payment required to access course content.');
        } else if (error.response?.status === 403) {
          toast.error('You do not have access to this course.');
          navigate('/courses');
        }

        setLoading(false);
      }
    };

    fetchData();
  }, [courseId, navigate]);

  // Handle payment success
  const handlePaymentSuccess = async () => {
    try {
      setAccessBlocked(false);
      // Refresh course data and fetch modules
      const courseDetails = await api.courses.getById(courseId);
      const data = await api.courses.getStructure(courseId);

      // Merge courseDetails with structure data
      if (data && data.course) {
        setCourse({
          ...courseDetails,
          ...data.course
        });
      } else {
        setCourse(courseDetails);
      }

      if (data && Array.isArray(data.modules)) {
        setModules(data.modules);
      }

      setProgress(data && typeof data.progress === 'number' ? data.progress : 0);

      if (data && data.modules && data.modules.length > 0) {
        setOpenModules({ [data.modules[0].id]: true });
      }

      toast.success('Payment successful! You now have access to the course content.');
    } catch (error) {
      console.error('Error refreshing course data:', error);
      toast.success('Payment successful! Please refresh the page to access course content.');
    }
  };

  const toggleModule = (moduleId) => {
    setOpenModules(prev => ({
      ...prev,
      [moduleId]: !prev[moduleId]
    }));
  };

  const getLessonIcon = (type, locked) => {
    if (locked) return <FiLock />;

    switch (type) {
      case 'video':
        return <FiPlay />;
      case 'text':
        return <FiFileText />;
      case 'interactive':
        return <FiPlay />;
      default:
        return <FiFileText />;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div>Loading course modules...</div>
      </Layout>
    );
  }

  // **PAYMENT PROTECTION**: Show payment required view
  if (accessBlocked && course) {
    return (
      <Layout>
        <PaymentBlockedContainer>
          <PaymentBlockedIcon />
          <PaymentBlockedTitle>Payment Required</PaymentBlockedTitle>
          <PaymentBlockedMessage>
            This course requires payment before you can access the learning materials.
            Please purchase the course to continue with your learning journey.
          </PaymentBlockedMessage>
          <PaymentButton onClick={() => setShowPaymentModal(true)}>
            Purchase Course (₹{course.price})
          </PaymentButton>
          <BackButton to="/courses">← Back to Courses</BackButton>
        </PaymentBlockedContainer>

        {/* Payment Modal */}
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          course={course}
          onSuccess={handlePaymentSuccess}
        />
      </Layout>
    );
  }

  // Additional safety check
  if (!course) {
    return (
      <Layout>
        <div>No course data available</div>
      </Layout>
    );
  }

  // Safely extract gradient colors
  const gradientStart = course && course.gradientStart ? course.gradientStart : '#0AFF70';
  const gradientEnd = course && course.gradientEnd ? course.gradientEnd : '#09B6FF';
  const courseTitle = course && course.title ? course.title : 'Course';

  return (
    <Layout>
      <ModulesHeader>
        <CourseImage gradient={`linear-gradient(135deg, ${gradientStart}, ${gradientEnd})`}>
          <CourseImageLetter>{courseTitle.charAt(0)}</CourseImageLetter>
        </CourseImage>

        <CourseInfo>
          <CourseTitle>{course?.title || 'Course Title'}</CourseTitle>
          <CourseDescription>{course?.description || 'No description available.'}</CourseDescription>

          <CourseStats>
            <Stat>
              <FiClock />
              {course?.duration_text || 'N/A'}
            </Stat>
            <Stat>
              <FiFileText />
              {course?.total_modules || 0} Modules
            </Stat>
            <Stat>
              <FiPlay />
              {course?.total_lessons || 0} Lessons
            </Stat>
          </CourseStats>
        </CourseInfo>

        <ProgressContainer>
          <ProgressText>
            <span>Course Progress</span>
            <span className="value">{progress}%</span>
          </ProgressText>
          <ProgressBar>
            <ProgressFill progress={progress} />
          </ProgressBar>
        </ProgressContainer>
      </ModulesHeader>

      <ModulesGrid>
        {modules.map((module) => (
          <Module
            key={module.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <ModuleHeader onClick={() => toggleModule(module.id)}>
              <ModuleTitle>
                <ModuleNumber completed={module.completed}>
                  {module.completed ? <FiCheck /> : module.number}
                </ModuleNumber>
                {module.title}
              </ModuleTitle>

              <ModuleInfo>
                <Stat>
                  <FiClock />
                  {module.duration}
                </Stat>

                <ModuleCompletion completed={module.completed}>
                  {module.completed ? (
                    <>
                      <FiCheck />
                      Completed
                    </>
                  ) : (
                    <>
                      In Progress
                    </>
                  )}
                </ModuleCompletion>
              </ModuleInfo>
            </ModuleHeader>

            <ModuleContent isOpen={openModules[module.id]}>
              {module.lessons && module.lessons.length > 0 ? (
                <LessonsList>
                  {module.lessons.map((lesson) => (
                    <Lesson
                      key={lesson.id}
                      to={lesson.locked ? '#' : `/learning/${courseId}/${lesson.id}`}
                      completed={lesson.completed}
                      locked={lesson.locked}
                      current={!lesson.completed && !lesson.locked}
                    >
                      <LessonType locked={lesson.locked}>
                        {getLessonIcon(lesson.type, lesson.locked)}
                      </LessonType>

                      <LessonContent>
                        <LessonTitle>{lesson.title}</LessonTitle>
                        <LessonDuration>
                          <FiClock />
                          {lesson.duration}
                        </LessonDuration>
                      </LessonContent>

                      <LessonStatus completed={lesson.completed}>
                        {lesson.completed ? <FiCheck /> : null}
                      </LessonStatus>

                      {lesson.locked && <LockedIcon />}
                    </Lesson>
                  ))}
                </LessonsList>
              ) : (
                <div style={{ padding: '16px 0' }}>
                  No lessons available for this module yet.
                </div>
              )}

              {module.quiz ? (
                <QuizLink
                  to={module.quiz.locked ? '#' : `/learning/${courseId}/${module.id}/quiz`}
                  completed={module.quiz.completed}
                  locked={module.quiz.locked}
                >
                  <QuizIcon locked={module.quiz.locked}>
                    {module.quiz.locked ? <FiLock /> : <FiFileText />}
                  </QuizIcon>

                  <LessonContent>
                    <LessonTitle>{module.quiz.title}</LessonTitle>
                    <LessonDuration>
                      <FiClock />
                      {module.quiz.duration}
                    </LessonDuration>
                  </LessonContent>

                  <LessonStatus completed={module.quiz.completed}>
                    {module.quiz.completed ? <FiCheck /> : null}
                  </LessonStatus>
                </QuizLink>
              ) : null}
            </ModuleContent>
          </Module>
        ))}
      </ModulesGrid>

      {modules.length > 0 && (
        <FinalQuizButton
          to={progress === 100 ? `/learning/${courseId}/final-quiz` : "#"}
          locked={progress !== 100}
        >
          {course?.has_passed_final_quiz ? (
            <>
              <FiCheck />
              Final Assessment Completed
            </>
          ) : (
            <>
                Take Final Assessment
                {progress !== 100 && <LockedIcon />}
            </>
          )}
        </FinalQuizButton>
      )}

      {/* Payment Modal for course access */}
      {course && (
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          course={course}
          onSuccess={handlePaymentSuccess}
        />
      )}
    </Layout>
  );
};

export default ModulesList;
